import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import {
  UserProfileContext,
  type UserProfile,
  type UserProfileActions,
  type UserProfileValidationError,
} from './UserProfileContext';
import { useApiQuery } from '../helpers/api-hooks';
import gql from 'graphql-tag';
import { has, isEmpty, isString, keys, mapKeys, merge, pick } from 'lodash';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { authUpdateAttributes } from '../helpers/api-helpers';
import { differenceInYears } from 'date-fns';
import Clarity from '@microsoft/clarity';
import { identify } from '../helpers/analytics';
import type { User } from '../App.interfaces';

const UserProfileFields = `
  firstName
  lastName
  middleName
  email
  phone
  address {
    streetAddress
    locality
    region
    postalCode
    country
  }
  birthdate
  ssn
  driversLicense
  gender
  binaryGender
  ethnicity
  eyeColor
  hairColor
  height
  weight
  customData {
    californiaBoaterCardApplicationId
    floridaFWCPIN
    goOutdoorsID
    arkansasCustomerId
    reasonForCompletingCourse
  }
`;

const GetUserProfileContextData = gql`
  query GetUserProfileContextData($userId: ID!) {
    user(id: $userId) {
      id
      profile {
        ${UserProfileFields}
      }
    }
  }
`;

const ValidateUserProfile = gql`
  query ValidateUserProfile($userId: ID!, $profile: UserProfileInput!) {
    userValidateProfile(id: $userId, profile: $profile) {
      valid
      errors {
        path
        message
      }
    }
  }
`;

const UpdateUserProfile = gql`
  mutation UpdateUserProfile($userId: ID!, $profile: UserProfileInput!) {
    user: userUpdateProfile(id: $userId, profile: $profile) {
      id
      profile {
        ${UserProfileFields}
      }
    }
  }
`;

const CalculatedFields = ['age'];

export function enrichProfile(profile: any): UserProfile {
  profile = {
    ...profile,
  };

  if (isString(profile.birthdate)) {
    profile.birthdate = profile.birthdate ? new Date(profile.birthdate) : null;
  }

  profile.age = profile.birthdate
    ? differenceInYears(new Date(), profile.birthdate)
    : null;

  return profile;
}

export function UserProfileServiceProvider({
  userId,
  children,
}: PropsWithChildren<{ userId: string }>) {
  const { refetch, data, loading } = useApiQuery<{
    user: User & { profile: UserProfile };
  }>(GetUserProfileContextData, {
    variables: {
      userId,
    },
    skip: !userId,
  });
  const [state, setState] = useState<{
    saving: boolean;
    dirty: boolean;
    profile: UserProfile;
    localEdits: UserProfile;
    cognitoEdits: { [name: string]: string };
    validationErrors: UserProfileValidationError[];
  }>(() => ({
    saving: false,
    dirty: false,
    profile: data ? enrichProfile(data.user.profile) : {},
    localEdits: {},
    cognitoEdits: {},
    validationErrors: [],
  }));

  // Take in each new query result while rendering, not in an effect, so
  // nothing below ever renders without a profile the query already has.
  const [receivedData, setReceivedData] = useState(data);
  if (data !== receivedData) {
    setReceivedData(data);
    if (data) {
      setState((state) => ({
        ...state,
        profile: enrichProfile(data.user.profile),
      }));
    }
  }

  const apiClient = useApolloClient();

  // Uniquely identify the user in Clarity and PostHog
  useEffect(() => {
    if (userId) {
      Clarity.identify(userId);
      identify(userId);
    }
  }, [userId]);

  const [updateUserProfile] = useMutation(UpdateUserProfile);

  const actions = useMemo(
    () => ({
      updateProfile(update: UserProfile) {
        const cognitoFieldMappings = {
          firstName: 'given_name',
          lastName: 'family_name',
          email: 'email',
        };
        const newCognitoEdits = mapKeys(
          pick(update, keys(cognitoFieldMappings)),
          (_, key) =>
            cognitoFieldMappings[key as keyof typeof cognitoFieldMappings],
        );
        setState((state) => ({
          ...state,
          dirty: true,
          cognitoEdits: merge({}, state.cognitoEdits, newCognitoEdits),
          localEdits: merge({}, state.localEdits, update),
        }));
      },
      async saveProfile(update: UserProfile, onSuccess?: () => void) {
        if (state.saving) {
          return;
        }

        update = merge({}, state.localEdits, update);

        setState((state) => ({
          ...state,
          saving: true,
          dirty: state.dirty || !!update,
          validationErrors: [],
          localEdits: update,
        }));

        const updateApiProfile = async () => {
          const validationErrors = isEmpty(update)
            ? []
            : await apiClient
                .query({
                  query: ValidateUserProfile,
                  variables: { userId, profile: update },
                  fetchPolicy: 'network-only',
                })
                .then(({ data }) => data.userValidateProfile.errors || []);

          if (!isEmpty(validationErrors)) {
            setState((state) => ({
              ...state,
              validationErrors,
              saving: false,
            }));
            return;
          }

          let profile = state.profile;

          if (!isEmpty(update)) {
            await updateUserProfile({
              variables: { userId, profile: update },
            }).then(({ data }) => {
              profile = enrichProfile(data.user.profile);
            });
          }

          setState((state) => ({
            ...state,
            profile,
            localEdits: {},
            cognitoEdits: {},
            saving: false,
            dirty: false,
          }));

          onSuccess?.();
        };

        if (!isEmpty(state.cognitoEdits) && state.profile.email) {
          authUpdateAttributes(state.profile.email, state.cognitoEdits).then(
            () => {
              updateApiProfile();
            },
          );
        } else {
          updateApiProfile();
        }
      },
      loadProfile() {
        refetch().then(({ data }) => {
          setState((state) => ({
            ...state,
            profile: enrichProfile(data.user.profile),
            localEdits: {},
            dirty: false,
          }));
        });
      },
      isCalculatedField(path: string) {
        return CalculatedFields.includes(path);
      },
      isDirtyField(path: string) {
        return has(state.localEdits, path);
      },
      get savedProfile() {
        return state.profile;
      },
    }),
    [
      refetch,
      updateUserProfile,
      state.localEdits,
      state.cognitoEdits,
      userId,
      state.profile,
      state.saving,
      apiClient,
    ],
  );

  const context = useMemo<UserProfile & UserProfileActions>(
    () => ({
      ...enrichProfile(
        merge(
          {
            loading,
            saving: state.saving,
            dirty: state.dirty,
            validationErrors: state.validationErrors,
          },
          state.profile,
          state.localEdits,
        ),
      ),
      ...actions,
    }),
    [
      loading,
      state.profile,
      state.localEdits,
      actions,
      state.saving,
      state.dirty,
      state.validationErrors,
    ],
  );

  return (
    <UserProfileContext.Provider value={context}>
      {children}
    </UserProfileContext.Provider>
  );
}
